/**
 * E1E2DestinationForm Component
 *
 * A form component for submitting E1/E2 destination details including IP addresses and justification.
 * The form captures central ID, target IP, and description, then submits the data through a review task workflow.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Function} props.switchE1E2DestinationForm - Callback function to toggle/switch back from the E1E2 destination form
 * @returns {React.ReactElement} A form surface component for E1/E2 destination submission
 *
 * @example
 * <E1E2DestinationForm switchE1E2DestinationForm={() => setShowForm(false)} />
 *
 * Features:
 * - Form validation using react-hook-form
 * - IPv4 address validation for target IP field
 * - Central ID selection via CentralIdField component
 * - Justification textarea for submission reason
 * - Loading state management during submission
 * - Success/error toast notifications
 * - Form reset functionality with key regeneration for uncontrolled fields
 * - Responsive layout (textarea rows adjust based on window width)
 */

import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useOutletContext } from 'react-router-dom'
import {
  Label,
  Input,
  ComponentLevelNotification as Alert,
  Textarea,
  Button,
  Surface,
} from '@americanexpress/dls-react'
import { IconBackIos } from '@americanexpress/dls-icons'
import { toast } from 'react-toastify'
import CentralIdField from './common/select/CentralIdField'
import { useGetRealUserInfo } from './common/RealUserInfoContext'
import { generateUUID } from './utilityFunctions'
import styles from './scss/E1E2DestinationForm.module.scss'

export default function E1E2DestinationForm({ switchE1E2DestinationForm }) {
  const [state, setState] = useState({
    isSubmitting: false,
    keyForUnControlledFields: 0,
  })

  const { lockAwareFetch } = useOutletContext()

  const creatorADSID = useGetRealUserInfo(({ adsid }) => adsid ?? '')

  const { isSubmitting, keyForUnControlledFields } = state

  const {
    register,
    handleSubmit,
    trigger,
    formState: { errors },
    getValues,
    setValue,
    setError,
    control,
    reset,
  } = useForm({
    defaultValues: {},
  })

  const onSubmit = values => {
    setState(prevState => ({ ...prevState, isSubmitting: true }))

    const targetFlow = 'StagingInternetGatingDestList'

    const callbackPayload = JSON.stringify({
      action: 'add',
      targetFlow,
      duration: 0,
      ...values,
      centralId: values.centralId.value,
    })

    lockAwareFetch(`${window.env?.VITE_IAR_API}/proxy-api`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        url: `${window.env.n8nApiUrl}/webhook/public/reviewhub-create-task`,
        method: 'POST',
        payload: {
          task_id: generateUUID(),
          task_title: `Add IP in EDL - ${targetFlow}`,
          task_description: values.description,
          task_type: 2,
          callback_payload: callbackPayload,
          creator_ecn: creatorADSID,
        },
      }),
    })
      .then(response => {
        if (response?.status) {
          setState(prevState => ({ ...prevState, isSubmitting: false }))
          toast.success('Submission successful! Your request has been sent for review.')
        } else {
          setState(prevState => ({ ...prevState, isSubmitting: false }))
          toast.error(
            `Submission failed: ${response?.errorInfo?.message || response?.message || 'Unknown error'}`
          )
        }
      })
      .catch(error => {
        console.error('Error during registration:', error)
        setState(prevState => ({ ...prevState, isSubmitting: false }))
        toast.error(`Submission failed: ${error.message || 'Unknown error'}`)
      })
  }

  return (
    <Surface variant='base' data-dls-mode='light' style={{ background: 'transparent' }}>
      <div className={styles.e1e2DestinationForm}>
        <div>
          <h2>Submit E1/E2 Destination</h2>
          <Button
            className={styles.backButton}
            variant='tertiary'
            icon={
              <IconBackIos
                color='white'
                size='lg'
                isFilled={false}
                title='Back'
                titleId='back-icon'
              />
            }
            onClick={() => switchE1E2DestinationForm()}
          >
            Back
          </Button>
        </div>
        <p>Please provide the required details and submit the form to continue.</p>

        <form id='e1e2DestinationForm' onSubmit={handleSubmit(onSubmit)}>
          <div className={styles.centralIdFieldWrapper}>
            <CentralIdField
              control={control}
              trigger={trigger}
              setValue={setValue}
              setError={setError}
              getValues={getValues}
              error={!!errors.centralId}
              errorMessage={errors.centralId?.message}
              required
            />
          </div>

          <div>
            <Label htmlFor='target'>Target (IP)</Label>
            <Input
              key={`target-${keyForUnControlledFields}`}
              className={styles.placeholderStyleWrapper}
              id='target'
              {...register('target', {
                required: 'IP address is required',
                pattern: {
                  value:
                    /^(?:(?:25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)(?:\.(?:25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)){3})$/,
                  message: 'Enter a valid IPv4 address (e.g., 192.168.1.1)',
                },
              })}
              onBlur={() => trigger('target')}
              status={errors.target ? 'error' : 'default'}
              placeholder='e.g., 192.168.1.1'
              name='target'
              type='text'
              required={true}
            />
            {errors.target && (
              <Alert data-dls-mode='dark' id='input-primary-error-alert' status='error'>
                {errors.target.message}
              </Alert>
            )}
          </div>

          <div>
            <Label htmlFor='description'>Justification</Label>
            <Textarea
              key={`description-${keyForUnControlledFields}`}
              id='description'
              {...register('description', {
                required: 'This field is required',
              })}
              onBlur={() => trigger('description')}
              status={errors.description ? 'error' : 'default'}
              placeholder='e.g., This IP is added for testing purposes.'
              rows={window.innerWidth <= 800 ? 10 : 15}
              name='description'
              type='text'
              required={true}
            />
            {errors.description && (
              <Alert id='input-primary-error-alert' status='error'>
                {errors.description.message}
              </Alert>
            )}
          </div>

          <div>
            <Button variant='primary' type='submit' disabled={isSubmitting}>
              Submit
            </Button>
            <Button
              variant='secondary'
              type='button'
              disabled={isSubmitting}
              onClick={() => {
                reset()
                setState(prevState => ({
                  ...prevState,
                  keyForUnControlledFields: prevState.keyForUnControlledFields + 1,
                })) // Reset uncontrolled fields by changing their key
              }}
            >
              Clear Values
            </Button>
          </div>
        </form>
      </div>
    </Surface>
  )
}
